import React, { useState, useMemo } from 'react';
import { 
  ChevronLeft, 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  Download,
  Music,
  ShoppingBag,
  Fuel,
  MapPin,
  Filter,
  BarChart3,
  PieChart as PieChartIcon,
  FileText
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { CardSkeleton } from '../common/Skeleton';
import ShowDetailModal from '../common/ShowDetailModal';
import { ExportGate } from '../subscription/FeatureGates';

const Summary = ({ 
  summary, 
  shows = [],
  generalExpenses = [],
  onNavigate,
  onExportShows,
  onExportExpenses,
  onExportInventory,
  onExportAll,
  onUpdateShow,
  loading,
  documents = []
}) => {
  const [selectedShow, setSelectedShow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [activeChart, setActiveChart] = useState('line');

  // Date filter logic
  const getFilteredData = useMemo(() => {
    if (!summary?.byDay) return { byDay: [], shows: [] };
    
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    let filteredDays = summary.byDay;
    
    switch (dateFilter) {
      case 'thisMonth':
        filteredDays = summary.byDay.filter(day => {
          const date = new Date(day.date);
          return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
        });
        break;
      case 'lastMonth':
        const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        filteredDays = summary.byDay.filter(day => {
          const date = new Date(day.date);
          return date.getMonth() === lastMonth && date.getFullYear() === lastMonthYear;
        });
        break;
      case 'thisYear':
        filteredDays = summary.byDay.filter(day => {
          const date = new Date(day.date);
          return date.getFullYear() === currentYear;
        });
        break;
      case 'lastYear':
        filteredDays = summary.byDay.filter(day => {
          const date = new Date(day.date);
          return date.getFullYear() === currentYear - 1;
        });
        break;
      case 'custom':
        if (customDateRange.start && customDateRange.end) {
          const startDate = new Date(customDateRange.start);
          const endDate = new Date(customDateRange.end);
          filteredDays = summary.byDay.filter(day => {
            const date = new Date(day.date);
            return date >= startDate && date <= endDate;
          });
        }
        break;
      default:
        break;
    }
    
    const filteredShows = filteredDays.flatMap(day => day.shows);
    
    return { byDay: filteredDays, shows: filteredShows };
  }, [summary, dateFilter, customDateRange]);

  // Helper to safely parse numbers
  const toNum = (val) => parseFloat(val) || 0;

  // Calculate filtered summary stats
  const filteredSummary = useMemo(() => {
    const { shows: filteredShows } = getFilteredData;
    
    const totalIncome = filteredShows.reduce((sum, show) => sum + toNum(show.income), 0);
    // Use cashMerch field for merch total (this is where Sales Mode saves the total)
    const totalMerch = filteredShows.reduce((sum, show) => sum + toNum(show.cashMerch), 0);
    const totalGuarantee = totalIncome - totalMerch;
    const totalShowExpenses = filteredShows.reduce((sum, show) => sum + toNum(show.expenses), 0);
    
    // Calculate general expenses for the filtered period
    let totalGeneralExpenses = 0;
    if (dateFilter === 'all') {
      totalGeneralExpenses = generalExpenses.reduce((sum, exp) => sum + toNum(exp.amount), 0);
    } else {
      const { byDay } = getFilteredData;
      if (byDay.length > 0) {
        const dates = byDay.map(d => new Date(d.date));
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        totalGeneralExpenses = generalExpenses
          .filter(exp => {
            const expDate = new Date(exp.date);
            return expDate >= minDate && expDate <= maxDate;
          })
          .reduce((sum, exp) => sum + toNum(exp.amount), 0);
      }
    }
    
    const totalExpenses = totalShowExpenses + totalGeneralExpenses;
    const netProfit = totalIncome - totalExpenses;
    const showCount = filteredShows.length;
    const avgPerShow = showCount > 0 ? netProfit / showCount : 0;
    
    return {
      totalIncome,
      totalGuarantee,
      totalMerch,
      totalShowExpenses,
      totalGeneralExpenses,
      totalExpenses,
      netProfit,
      showCount,
      avgPerShow
    };
  }, [getFilteredData, generalExpenses, dateFilter]);

  // Chart data preparation - merge show days with general expense days
  const lineChartData = useMemo(() => {
    // Create a map of all dates with their data
    const dateMap = new Map();
    
    // Add show data
    getFilteredData.byDay.forEach(day => {
      const dateKey = day.date;
      dateMap.set(dateKey, {
        date: dateKey,
        income: day.shows.reduce((sum, s) => sum + toNum(s.income), 0),
        showExpenses: day.shows.reduce((sum, s) => sum + toNum(s.expenses), 0),
        generalExpenses: 0,
        profit: toNum(day.dayProfit)
      });
    });
    
    // Add general expenses (filter by date range if needed)
    let expensesToInclude = generalExpenses;
    if (dateFilter !== 'all' && getFilteredData.byDay.length > 0) {
      const dates = getFilteredData.byDay.map(d => new Date(d.date));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      expensesToInclude = generalExpenses.filter(exp => {
        const expDate = new Date(exp.date);
        return expDate >= minDate && expDate <= maxDate;
      });
    }
    
    expensesToInclude.forEach(exp => {
      const dateKey = exp.date;
      if (dateMap.has(dateKey)) {
        // Add to existing day
        const existing = dateMap.get(dateKey);
        existing.generalExpenses += toNum(exp.amount);
        existing.profit -= toNum(exp.amount); // Reduce profit by general expense
      } else {
        // Create new day entry for expense-only day
        dateMap.set(dateKey, {
          date: dateKey,
          income: 0,
          showExpenses: 0,
          generalExpenses: toNum(exp.amount),
          profit: -toNum(exp.amount)
        });
      }
    });
    
    // Convert to array and sort by date
    return Array.from(dateMap.values())
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(day => ({
        date: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        income: day.income,
        expenses: day.showExpenses + day.generalExpenses,
        showExpenses: day.showExpenses,
        generalExpenses: day.generalExpenses,
        profit: day.profit
      }));
  }, [getFilteredData, generalExpenses, dateFilter]);

  const expensePieData = useMemo(() => {
    const { shows: filteredShows } = getFilteredData;
    
    const gas = filteredShows.reduce((sum, s) => sum + toNum(s.gas), 0);
    const hotel = filteredShows.reduce((sum, s) => sum + toNum(s.hotel), 0);
    const food = filteredShows.reduce((sum, s) => sum + toNum(s.food), 0);
    const tolls = filteredShows.reduce((sum, s) => sum + toNum(s.tolls), 0);
    const other = filteredShows.reduce((sum, s) => sum + toNum(s.otherExpenses), 0);
    const general = filteredSummary.totalGeneralExpenses;
    
    return [
      { name: 'Gas', value: gas, color: '#f59e0b' },
      { name: 'Hotel', value: hotel, color: '#8b5cf6' },
      { name: 'Food', value: food, color: '#10b981' },
      { name: 'Tolls', value: tolls, color: '#6366f1' },
      { name: 'Other', value: other, color: '#ec4899' },
      { name: 'General', value: general, color: '#ef4444' }
    ].filter(item => item.value > 0);
  }, [getFilteredData, filteredSummary]);

  const barChartData = useMemo(() => {
    return getFilteredData.shows
      .slice(-10)
      .map(show => ({
        name: show.title?.substring(0, 15) || 'Show',
        income: toNum(show.income),
        expenses: toNum(show.expenses),
        profit: toNum(show.profit)
      }));
  }, [getFilteredData]);

  const handleShowClick = (show) => {
     const handleShowClick = (show) => {
    // DEBUG: Log show data when clicked
    console.log('👆 [handleShowClick] Show clicked:', {
      showId: show.id,
      title: show.title || show.venue,
      dateKey: show.dateKey,
      cashMerch: show.cashMerch,
      merchSales: show.merchSales,
      merchSalesLength: show.merchSales?.length || 0,
      allKeys: Object.keys(show)
    });
    
    setSelectedShow(show);
    setIsModalOpen(true);
  };
    setSelectedShow(show);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedShow(null);
  };

  const handleExportPDF = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 pb-20 md:pb-0">
      {/* Mobile Sticky Header */}
      <div className="sticky top-0 z-50 bg-white dark:bg-gray-800 shadow-md md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => onNavigate('dashboard')}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 active:bg-gray-200 dark:active:bg-gray-600 transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Financial Summary</h1>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>
      </div>


      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 md:p-6 mb-6">
          {/* Header - Desktop Only */}
          <div className="hidden md:flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100">Financial Summary</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                {filteredSummary.showCount} shows • {dateFilter === 'all' ? 'All Time' : dateFilter.replace(/([A-Z])/g, ' $1').trim()}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onNavigate('dashboard')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>

          {/* Mobile Subheader */}
          <div className="md:hidden mb-4">
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {filteredSummary.showCount} shows • {dateFilter === 'all' ? 'All Time' : dateFilter.replace(/([A-Z])/g, ' $1').trim()}
            </p>
          </div>

          {/* Date Filter Section */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Filter size={18} className="text-gray-600 dark:text-gray-400" />
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">Filter by Date</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {['all', 'thisMonth', 'lastMonth', 'thisYear', 'lastYear', 'custom'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setDateFilter(filter)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    dateFilter === filter
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {filter === 'all' ? 'All Time' : 
                   filter === 'thisMonth' ? 'This Month' :
                   filter === 'lastMonth' ? 'Last Month' :
                   filter === 'thisYear' ? 'This Year' :
                   filter === 'lastYear' ? 'Last Year' : 'Custom'}
                </button>
              ))}
            </div>
            {dateFilter === 'custom' && (
              <div className="flex flex-wrap gap-3 mt-3">
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                />
                <span className="self-center text-gray-600 dark:text-gray-400">to</span>
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                />
              </div>
            )}
          </div>

          {/* Summary Cards - 6 Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6">
            {/* Guarantee Income */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between mb-1">
                <Music size={18} className="opacity-80" />
              </div>
              <p className="text-xs opacity-80">Guarantee</p>
              <p className="text-xl md:text-2xl font-bold">{formatCurrency(filteredSummary.totalGuarantee)}</p>
            </div>

            {/* Merch Income */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between mb-1">
                <ShoppingBag size={18} className="opacity-80" />
              </div>
              <p className="text-xs opacity-80">Merch Sales</p>
              <p className="text-xl md:text-2xl font-bold">{formatCurrency(filteredSummary.totalMerch)}</p>
            </div>

            {/* Show Expenses */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between mb-1">
                <Fuel size={18} className="opacity-80" />
              </div>
              <p className="text-xs opacity-80">Show Costs</p>
              <p className="text-xl md:text-2xl font-bold">{formatCurrency(filteredSummary.totalShowExpenses)}</p>
            </div>

            {/* General Expenses */}
            <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between mb-1">
                <TrendingDown size={18} className="opacity-80" />
              </div>
              <p className="text-xs opacity-80">General Exp.</p>
              <p className="text-xl md:text-2xl font-bold">{formatCurrency(filteredSummary.totalGeneralExpenses)}</p>
            </div>

            {/* Show Count */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between mb-1">
                <MapPin size={18} className="opacity-80" />
              </div>
              <p className="text-xs opacity-80">Total Shows</p>
              <p className="text-xl md:text-2xl font-bold">{filteredSummary.showCount}</p>
            </div>

            {/* Avg Per Show */}
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between mb-1">
                <BarChart3 size={18} className="opacity-80" />
              </div>
              <p className="text-xs opacity-80">Avg/Show</p>
              <p className="text-xl md:text-2xl font-bold">{formatCurrency(filteredSummary.avgPerShow)}</p>
            </div>
          </div>

          {/* Big Net Profit Card */}
          <div className={`bg-gradient-to-br ${filteredSummary.netProfit >= 0 ? 'from-purple-600 to-purple-700' : 'from-gray-600 to-gray-700'} text-white p-6 rounded-lg shadow mb-6`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm opacity-80 mb-1">Net Profit</p>
                <p className="text-4xl md:text-5xl font-bold">{formatCurrency(filteredSummary.netProfit)}</p>
                <p className="text-sm opacity-70 mt-2">
                  {formatCurrency(filteredSummary.totalIncome)} income − {formatCurrency(filteredSummary.totalExpenses)} expenses
                </p>
              </div>
              <div className="mt-4 md:mt-0">
                <DollarSign size={64} className="opacity-20" />
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 size={20} className="text-gray-600 dark:text-gray-400" />
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">Analytics</h3>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setActiveChart('line')}
                  className={`p-2 rounded-lg transition-colors ${activeChart === 'line' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
                  title="Trend Line"
                >
                  <TrendingUp size={18} />
                </button>
                <button
                  onClick={() => setActiveChart('bar')}
                  className={`p-2 rounded-lg transition-colors ${activeChart === 'bar' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
                  title="Bar Chart"
                >
                  <BarChart3 size={18} />
                </button>
                <button
                  onClick={() => setActiveChart('pie')}
                  className={`p-2 rounded-lg transition-colors ${activeChart === 'pie' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
                  title="Expense Breakdown"
                >
                  <PieChartIcon size={18} />
                </button>
              </div>
            </div>

            <div className="h-72 md:h-80">
              {activeChart === 'line' && lineChartData.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} tickFormatter={(v) => `$${v}`} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1f2937', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`$${value.toFixed(2)}`, '']}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} name="Income" />
                    <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444' }} name="Expenses" />
                    <Line type="monotone" dataKey="profit" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6' }} name="Profit" />
                  </LineChart>
                </ResponsiveContainer>
              )}

              {activeChart === 'bar' && barChartData.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} angle={-45} textAnchor="end" height={60} />
                    <YAxis stroke="#9ca3af" fontSize={12} tickFormatter={(v) => `$${v}`} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1f2937', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`$${value.toFixed(2)}`, '']}
                    />
                    <Legend />
                    <Bar dataKey="income" fill="#10b981" name="Income" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="profit" fill="#8b5cf6" name="Profit" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}

              {activeChart === 'pie' && expensePieData.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expensePieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={true}
                    >
                      {expensePieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1f2937', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`$${value.toFixed(2)}`, '']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}

              {((activeChart === 'line' && lineChartData.length === 0) ||
                (activeChart === 'bar' && barChartData.length === 0) ||
                (activeChart === 'pie' && expensePieData.length === 0)) && (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  <div className="text-center">
                    <BarChart3 size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No data available for this view</p>
                    <p className="text-sm">Add some shows to see analytics</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Export Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            <ExportGate>
              <button
                onClick={onExportShows}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Download size={16} />
                Export Shows
              </button>
            </ExportGate>
            <ExportGate>
              <button
                onClick={onExportExpenses}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
              >
                <Download size={16} />
                Export Expenses
              </button>
            </ExportGate>
            <ExportGate>
              <button
                onClick={onExportInventory}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                <Download size={16} />
                Export Inventory
              </button>
            </ExportGate>
            <ExportGate>
              <button
                onClick={onExportAll}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
              >
                <Download size={16} />
                Export All Data
              </button>
            </ExportGate>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-colors text-sm font-semibold"
            >
              <FileText size={16} />
              Print / PDF
            </button>
          </div>

          {/* Shows by Day */}
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              <Calendar size={24} />
              Shows Breakdown
            </h2>

            {getFilteredData.byDay.length === 0 ? (
              <div className="text-center py-12">
                <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {dateFilter === 'all' ? 'No shows added yet' : 'No shows in selected period'}
                </p>
                {dateFilter === 'all' && (
                  <button
                    onClick={() => onNavigate('calendar')}
                    className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add Your First Show
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {getFilteredData.byDay.map((dayData, index) => (
                  <div key={`${dayData.date}-${index}`} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                        {formatDate(dayData.date)}
                      </h3>
                      <div className="text-right">
                        <p className="text-sm text-gray-600 dark:text-gray-400">Day Profit</p>
                        <p className={`text-lg font-bold ${dayData.dayProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(dayData.dayProfit)}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {dayData.shows.map(show => (
                        <button
                          key={show.id}
                          onClick={() => handleShowClick(show)}
                          className="w-full text-left bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border-2 border-transparent hover:border-blue-500 dark:hover:border-blue-500 transition-all cursor-pointer"
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-1">{show.title}</h4>
                              {show.venue && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {show.venue}{show.city && `, ${show.city}`}
                                </p>
                              )}
                              {show.showTime && (
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                  Show: {show.showTime}
                                </p>
                              )}
                            </div>

                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Income</p>
                                <p className="font-semibold text-green-600 dark:text-green-400">
                                  {formatCurrency(show.income)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Expenses</p>
                                <p className="font-semibold text-red-600 dark:text-red-400">
                                  {formatCurrency(show.expenses)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Profit</p>
                                <p className={`font-bold ${show.profit >= 0 ? 'text-purple-600 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                  {formatCurrency(show.profit)}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                            Click to view details →
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Show Detail Modal */}
      <ShowDetailModal
        show={selectedShow}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUpdate={onUpdateShow}
        onNavigate={onNavigate}
        documents={documents}
      />
    </div>
  );
};

export default Summary;